import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, MenuItem, LinearProgress, Alert, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar,
  List, ListItem, ListItemText, ListItemAvatar, ListItemSecondaryAction,
  Divider, Tabs, Tab, FormControlLabel, Switch,
} from '@mui/material';
import { ContactEmergency, Add, Edit, Delete, Star, StarBorder, Close, Phone, Email as EmailIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const RELATIONSHIPS = ['Spouse', 'Partner', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'];

const AVATAR_COLORS = ['#1a237e','#b71c1c','#1b5e20','#e65100','#4a148c'];
function avatarColor(name: string) { return AVATAR_COLORS[(name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length]; }

interface Contact { id: string; employeeId: string; name: string; relationship: string; phone: string; alternatePhone?: string; email?: string; address?: string; isPrimary: boolean; notes?: string; }
interface EmployeeGroup { employeeId: string; employee: { firstName: string; lastName: string; department?: string; jobTitle?: string }; contacts: Contact[]; }

const emptyForm = (): Partial<Contact> => ({ relationship: 'Spouse', isPrimary: false });

export default function EmergencyContacts() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [myContacts, setMyContacts] = useState<Contact[]>([]);
  const [allGroups, setAllGroups] = useState<EmployeeGroup[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formDlg, setFormDlg] = useState<Partial<Contact> | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [mine, st] = await Promise.all([
        apiFetch('/api/emergency-contacts/mine'),
        apiFetch('/api/emergency-contacts/stats'),
      ]);
      setMyContacts(mine || []); setStats(st);
      if (isHR) {
        const all = await apiFetch('/api/emergency-contacts/all');
        setAllGroups(all || []);
      }
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!formDlg?.name || !formDlg?.phone) return;
    try {
      formDlg.id
        ? await apiFetch(`/api/emergency-contacts/${formDlg.id}`, { method: 'PATCH', body: JSON.stringify(formDlg) })
        : await apiFetch('/api/emergency-contacts', { method: 'POST', body: JSON.stringify(formDlg) });
      setFormDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const remove = async () => {
    if (!deleteId) return;
    try { await apiFetch(`/api/emergency-contacts/${deleteId}`, { method: 'DELETE' }); setDeleteId(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  const setPrimary = async (c: Contact) => {
    try { await apiFetch(`/api/emergency-contacts/${c.id}`, { method: 'PATCH', body: JSON.stringify({ isPrimary: true }) }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const ContactCard = ({ c, editable }: { c: Contact; editable?: boolean }) => (
    <Card variant="outlined" sx={{ mb: 1.5, border: c.isPrimary ? '2px solid #1a237e' : undefined }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Avatar sx={{ bgcolor: avatarColor(c.name), width: 40, height: 40 }}>
              {c.name[0].toUpperCase()}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={700}>{c.name}</Typography>
                {c.isPrimary && <Chip label="Primary" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
              </Box>
              <Typography variant="body2" color="text.secondary">{c.relationship}</Typography>
              <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2">{c.phone}</Typography>
                  {c.alternatePhone && <Typography variant="body2" color="text.secondary">/ {c.alternatePhone}</Typography>}
                </Box>
                {c.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2">{c.email}</Typography>
                </Box>}
                {c.address && <Typography variant="caption" color="text.secondary">{c.address}</Typography>}
                {c.notes && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{c.notes}</Typography>}
              </Box>
            </Box>
          </Box>
          {editable && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {!c.isPrimary && <Tooltip title="Set as Primary"><IconButton size="small" onClick={() => setPrimary(c)}><StarBorder fontSize="small" /></IconButton></Tooltip>}
              {c.isPrimary && <Tooltip title="Primary Contact"><IconButton size="small" disabled><Star fontSize="small" color="primary" /></IconButton></Tooltip>}
              <Tooltip title="Edit"><IconButton size="small" onClick={() => setFormDlg({ ...c })}><Edit fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteId(c.id)}><Delete fontSize="small" /></IconButton></Tooltip>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContactEmergency sx={{ fontSize: 32, color: '#c62828' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Emergency Contacts</Typography>
            <Typography variant="body2" color="text.secondary">Who to contact in case of emergency</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#c62828' }}
          onClick={() => setFormDlg(emptyForm())}>Add Contact</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#1a237e">{stats.employeesWithContacts}</Typography>
                <Typography variant="caption" color="text.secondary">Employees with Contacts</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#37474f">{stats.totalContacts}</Typography>
                <Typography variant="caption" color="text.secondary">Total Contacts Registered</Typography>
              </CardContent>
            </Card>
          </Grid>
          {isHR && (
            <Grid size={{ xs: 12, sm: 6 }}>
              {stats.employeesWithContacts < 5 && (
                <Alert severity="warning" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  Remind employees to add emergency contacts — only {stats.employeesWithContacts} {stats.employeesWithContacts === 1 ? 'has' : 'have'} registered contacts.
                </Alert>
              )}
            </Grid>
          )}
        </Grid>
      )}

      {isHR && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`My Contacts (${myContacts.length})`} />
          <Tab label={`All Employees (${allGroups.length})`} />
        </Tabs>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── MY CONTACTS ── */}
      {(tab === 0 || !isHR) && (
        <Box>
          {myContacts.length === 0 && !loading ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <ContactEmergency sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography color="text.secondary" gutterBottom>No emergency contacts added yet.</Typography>
              <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#c62828' }} onClick={() => setFormDlg(emptyForm())}>
                Add Your First Contact
              </Button>
            </Paper>
          ) : (
            <Box sx={{ maxWidth: 600 }}>
              {myContacts.map(c => <ContactCard key={c.id} c={c} editable />)}
            </Box>
          )}
        </Box>
      )}

      {/* ── ALL EMPLOYEES (HR) ── */}
      {isHR && tab === 1 && (
        <Box>
          {allGroups.map(g => (
            <Paper key={g.employeeId} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: avatarColor(`${g.employee?.firstName}${g.employee?.lastName}`), fontSize: '0.8rem' }}>
                  {g.employee?.firstName?.[0]}{g.employee?.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography fontWeight={700}>{g.employee?.firstName} {g.employee?.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">{g.employee?.jobTitle} · {g.employee?.department}</Typography>
                </Box>
                <Chip label={`${g.contacts.length} contact${g.contacts.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 'auto' }} />
              </Box>
              {g.contacts.length === 0 ? (
                <Typography variant="body2" color="error.main" sx={{ pl: 1 }}>⚠️ No emergency contact registered</Typography>
              ) : (
                <Box sx={{ pl: 1 }}>
                  {g.contacts.map(c => (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5, borderTop: '1px solid #f5f5f5' }}>
                      {c.isPrimary && <Star sx={{ fontSize: 14, color: '#1a237e' }} />}
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>{c.name}</Typography>
                      <Chip label={c.relationship} size="small" variant="outlined" />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2">{c.phone}</Typography>
                      </Box>
                      {c.email && <Typography variant="caption" color="text.secondary">{c.email}</Typography>}
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          ))}
          {allGroups.length === 0 && !loading && (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No emergency contacts on record.</Paper>
          )}
        </Box>
      )}

      {/* ── ADD / EDIT FORM ── */}
      <Dialog open={!!formDlg} onClose={() => setFormDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {formDlg?.id ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormDlg(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Full Name *" fullWidth value={formDlg?.name ?? ''}
              onChange={e => setFormDlg(p => ({ ...p, name: e.target.value }))} />
            <TextField label="Relationship" select fullWidth value={formDlg?.relationship ?? 'Spouse'}
              onChange={e => setFormDlg(p => ({ ...p, relationship: e.target.value }))}>
              {RELATIONSHIPS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField label="Phone *" fullWidth value={formDlg?.phone ?? ''}
              onChange={e => setFormDlg(p => ({ ...p, phone: e.target.value }))} />
            <TextField label="Alternate Phone" fullWidth value={formDlg?.alternatePhone ?? ''}
              onChange={e => setFormDlg(p => ({ ...p, alternatePhone: e.target.value }))} />
            <TextField label="Email" type="email" fullWidth value={formDlg?.email ?? ''}
              onChange={e => setFormDlg(p => ({ ...p, email: e.target.value }))} />
            <TextField label="Address" fullWidth multiline rows={2} value={formDlg?.address ?? ''}
              onChange={e => setFormDlg(p => ({ ...p, address: e.target.value }))} />
            <TextField label="Notes" fullWidth value={formDlg?.notes ?? ''}
              onChange={e => setFormDlg(p => ({ ...p, notes: e.target.value }))} />
            <FormControlLabel control={<Switch checked={formDlg?.isPrimary ?? false}
              onChange={e => setFormDlg(p => ({ ...p, isPrimary: e.target.checked }))} />}
              label="Set as primary contact" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDlg(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#c62828' }} onClick={save}
            disabled={!formDlg?.name?.trim() || !formDlg?.phone?.trim()}>
            {formDlg?.id ? 'Update' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRM ── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Contact?</DialogTitle>
        <DialogContent><Typography>This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
